import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Check, ChevronRight } from '@mui/icons-material';
import { Box, Grid, Typography, Chip } from '@mui/material';
import { News } from './NewsList';
import { response } from '../../../api/db';
import { getMemberNewsList } from '../../../api/db/news';
import {
  primaryColor,
  primary10,
  gray20,
  gray90,
  gray100,
  green10,
  green100
} from '../../../constants/Colors';
import { common } from '../../../modules/error';
import { MemberNewsListGetType } from '../../../validation/news/newsType';
import { sentryLog } from '../../../config/sentry';

const styles = {
  wrapper: {
    '@media screen and (max-width:600px)': {
      padding: '24px 16px'
    },
    padding: '32px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px',
      mb: '16px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: 500,
    mb: '24px'
  },
  row: {
    width: '100%',
    alignItems: 'center',
    borderBottom: '1px solid #D8D9DB',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: '500',
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0.4px',
    color: gray100,
    padding: '24px 0px',
    textDecoration: 'none',
    '@media screen and (max-width:600px)': {
      padding: '16px 0'
    }
  },
  tag: {
    borderRadius: '26px',
    fontSize: '12px',
    fontWeight: 500,
    height: '22px',
    mr: '8px'
  },
  date: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '22px',
    color: gray90,
    mr: '16px'
  },
  tabs: {
    borderBottom: '1px solid #DBDBE0'
  },
  tab: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px'
    },
    '&.Mui-selected': {
      color: primaryColor
    },
    padding: '9px 16px',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.4px'
  },
  tags: {
    display: 'flex',
    flexDirection: 'row',
    rowGap: '8px',
    mr: '8px'
  },
  body: {
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      rowGap: '8px'
    },
    display: 'flex',
    flexDirection: 'row'
  },
  checkIcon: {
    color: gray100,
    height: '80px',
    width: '80px'
  },
  nullText: {
    color: gray100,
    fontSize: '24px',
    mt: '16px',
    '@media screen and (max-width:600px)': {
      width: '170px'
    }
  }
};

const MemberNewsItem = (news: News): React.ReactElement => {
  const chipColor = (tag: string) => {
    switch (tag) {
      case '既読':
        return { color: gray90, backgroundColor: gray20 };
      case '未読':
        return { color: green100, backgroundColor: green10 };
      default:
        return { color: primaryColor, backgroundColor: primary10 };
    }
  };

  return (
    <Box
      sx={styles.row}
      component={'a'}
      href={'/news/member/' + news.id}
      target="_blank"
    >
      <Box sx={styles.body}>
        <Box display="flex" flexDirection={'row'}>
          <Typography sx={styles.date}>{news.date}</Typography>
          <Box sx={styles.tags}>
            {news.tag?.map((tag, index) => (
              <Chip
                label={tag}
                sx={{ ...chipColor(tag), ...styles.tag }}
                key={index}
              />
            ))}
          </Box>
        </Box>
        <Typography>{news.title}</Typography>
      </Box>
      <ChevronRight />
    </Box>
  );
};

interface Props {
  currentPage: number;
  setErrorText: (value: string) => void;
  setLoading: (value: boolean) => void;
  setOpen: (value: boolean) => void;
  setTotalPages: (value: number) => void;
}

export const MemberNewsItemList = (props: Props): React.ReactElement => {
  const { currentPage, setErrorText, setLoading, setOpen, setTotalPages } =
    props;

  const [displayList, setDisplayList] = useState<News[]>([]);
  const [showContent, setShowContent] = useState<boolean>(false);

  // ページを変更した時、ニュースを取得
  useEffect((): void => {
    void (async () => {
      setLoading(true);
      try {
        const limit = 10;
        const offset = (currentPage - 1) * limit;

        // ニュース取得
        const response = await getMemberNewsList(limit, offset);
        const fetchedData = response.data;

        // ページネーション
        const totalPages = Math.ceil(fetchedData.total_count / limit);
        setTotalPages(totalPages);

        // 表示用データに変換
        const displayList = fetchedData.member_news.map(
          (item: MemberNewsListGetType) => {
            // 日付の表示形式を変更
            const updateDate = new Date(item.updated_at);
            const month = updateDate.getMonth() + 1;
            const formattedMonth = month + 1 < 10 ? '0' + month : '' + month;
            const day = updateDate.getDate();
            const formattedDay = day < 10 ? '0' + day : '' + day;
            const formattedUpdatedDate = `${updateDate.getFullYear()}/${formattedMonth}/${formattedDay}`;

            // タグ
            const tag = item.read_flg ? ['既読'] : ['未読'];

            return {
              id: item.id,
              title: item.title,
              date: formattedUpdatedDate,
              tag: tag
            };
          }
        );
        setDisplayList(displayList);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const loginErrorResponse = axiosError.response as response;
          setErrorText(loginErrorResponse.data.message);
          setOpen(true);
        } else {
          setErrorText(common);
          setOpen(true);
        }
      } finally {
        setLoading(false);
        setShowContent(true);
      }
    })();
  }, [currentPage]);

  return (
    <>
      {showContent && displayList.length === 0 ? (
        <Grid
          container
          alignItems="center"
          direction="column"
          sx={{
            mt: '160px',
            '@media screen and (max-width:600px)': {
              mt: '104px'
            }
          }}
        >
          <Check sx={styles.checkIcon} />
          <Typography sx={styles.nullText}>
            会員個別情報はまだありません
          </Typography>
        </Grid>
      ) : (
        displayList.map((news) => <MemberNewsItem {...news} key={news.id} />)
      )}
    </>
  );
};
